<template>
  <div class="shop-cart">
    <a-popover trigger="click" :visible="visible" :getPopupContainer="triggerNode => triggerNode.parentNode">
      <a-button v-if="componentType === 'workflowBtn'" type="link" @click="visible = !visible">{{ $t('common.add_resource_from_shopcart') }}</a-button>
      <div v-else class="d-flex align-items-center justify-content-center h-100">
        <a-tooltip :title="$t('scope.shopcart')" placement="right">
          <a-badge :count="total" :overflowCount="99">
            <a-icon type="shopping-cart" @click="visible = !visible" style="font-size: 22px;" />
          </a-badge>
        </a-tooltip>
      </div>
      <template v-slot:content>
        <div class="shop-cart-content">
          <div class="top d-flex align-items-center justify-content-between">
            <h3>{{ $t('scope.shopcart') }}</h3>
            <div>
              <a-tooltip :title="isNoChooseData ? $t('scope.shopcart.please_choose_resource') : ''">
                <a-button
                  type="link"
                  :disabled="isNoChooseData"
                  :loading="shopCartCreateLoading"
                  @click="handleClickCreateHandle">
                  {{ isOpenOrderSetWorkflow ? (componentType === 'workflowBtn' ? $t('common_114') : $t('compute.text_288')) : $t('common.create') }}
                </a-button>
              </a-tooltip>
              <a-tooltip v-if="componentType !== 'workflowBtn'" :title="isNoChooseData ? $t('scope.shopcart.please_choose_resource') : ''">
                <a-button
                  type="link"
                  :disabled="isNoChooseData"
                  @click="handleClickRemoveHandle">
                  {{ $t('common.remove') }}
                </a-button>
              </a-tooltip>
            </div>
          </div>
          <div class="bottom">
            <div class="no-data" v-show="isNoData">{{$t('common.notData')}}</div>
            <vxe-grid
              v-show="!isNoData"
              row-id="id"
              ref="tableRef"
              max-height="300"
              resizable
              :sync-resize="visible"
              :columns="tableColumns"
              :data="shopCartTableUserData"
              @checkbox-all="selectAllChangeHandle"
              @checkbox-change="selectChangeHandle">
              <template #pager>
                <vxe-pager
                  :layouts="['Total']"
                  :current-page.sync="shopCartTablePage.currentPage"
                  :page-size.sync="shopCartTablePage.pageSize"
                  :total="shopCartTablePage.total" />
              </template>
            </vxe-grid>
          </div>
        </div>
      </template>
    </a-popover>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import storage from '@/utils/storage'
import { HYPERVISORS_MAP } from '@/constants'
import { WORKFLOW_TYPES } from '@/constants/workflow'
import BrandIcon from '@/sections/BrandIcon'
import { Manager } from '@/utils/manager'
import workflowMixin from '@/mixins/workflow'
import { sizestr, uuid } from '@/utils/utils'

export default {
  name: 'ShopCart',
  mixins: [workflowMixin],
  props: {
    componentType: String, // navIcon, workflowBtn
    orderSetResId: String,
  },
  data () {
    return {
      visible: false,
      shopCartCreateLoading: false,
      selectRecords: [],
      tableColumns: [
        { type: 'checkbox', width: 40 },
        {
          field: 'resource',
          title: this.$t('common.resource_type'),
          width: 100,
          slots: {
            default: ({ row }) => {
              return [
                this.$t(`dictionary.${row.resource.slice(0, -1)}`),
              ]
            },
          },
        },
        {
          field: 'name',
          title: this.$t('common.name'),
          minWidth: 100,
          slots: {
            default: ({ row }) => {
              return [
                row.parameter?.generate_name,
              ]
            },
          },
        },
        {
          field: 'instance_type',
          title: this.$t('table.title.flavor'),
          showOverflow: 'ellipsis',
          minWidth: 110,
          sortable: true,
          slots: {
            default: ({ row }) => {
              const { disks = [], vcpu_count, vmem_size } = row.parameter
              const disk = disks.map(o => o.size).reduce((acc, cur) => acc + cur, 0)
              const config = vcpu_count + 'C' + (vmem_size / 1024) + 'G' + (disk ? sizestr(disk, 'M', 1024) : '')
              return [
                <div class='text-truncate' style={{ color: '#53627C' }}>{config}</div>,
              ]
            },
          },
        },
        {
          field: 'hypervisor',
          title: this.$t('table.title.brand'),
          width: 60,
          slots: {
            default: ({ row }, h) => {
              let { hypervisor } = row.parameter
              if (!hypervisor) return '-'
              if (row.parameter.extraData?.formType === 'private' && hypervisor === 'kvm') {
                hypervisor = 'cloudpods'
              }
              const brand = HYPERVISORS_MAP[hypervisor].brand
              if (!brand) return '-'
              return [
                <BrandIcon name={brand} />,
              ]
            },
          },
        },
        {
          field: 'billing_type',
          title: this.$t('table.title.bill_type'),
          width: 80,
          slots: {
            default: ({ row }, h) => {
              const ret = []
              const { duration } = row.parameter
              const billing_type = duration ? 'prepaid' : 'postpaid'
              if (billing_type === 'postpaid') {
                ret.push(<div style={{ color: '#0A1F44' }}>{this.$t('billingType.postpaid')}</div>)
              } else if (billing_type === 'prepaid') {
                ret.push(<div style={{ color: '#0A1F44' }}>{this.$t('billingType.prepaid')}</div>)
              }
              return ret
            },
          },
        },
        { field: 'count', minWidth: 50, title: this.$t('scope.text_838') },
      ],
    }
  },
  computed: {
    ...mapGetters(['userInfo']),
    ...mapState('shopcart', {
      shopCartTableData: state => state.shopCarts,
    }),
    shopCartTableUserData () {
      return this.shopCartTableData.filter(o => o.user_id === this.userInfo?.id)
    },
    total () {
      return this.shopCartTableUserData?.length || 0
    },
    isNoData () {
      return this.total === 0
    },
    isNoChooseData () {
      return this.selectRecords?.length === 0
    },
    shopCartTablePage () {
      return {
        total: this.total,
        currentPage: 1,
        pageSize: 100,
      }
    },
    isOpenOrderSetWorkflow () {
      return this.checkWorkflowEnabled(WORKFLOW_TYPES.EXECUTE_RESOURCE_ORDER_SET)
    },
  },
  watch: {
    total (v) {
      this.visible = v > 0
      storage.set('__oc_shop_carts', this.shopCartTableUserData)
    },
  },
  methods: {
    selectAllChangeHandle () {
      const records = this.$refs.tableRef.getCheckboxRecords(true)
      this.selectRecords = records
    },
    selectChangeHandle () {
      const records = this.$refs.tableRef.getCheckboxRecords(true)
      this.selectRecords = records
    },
    handleClickRemoveHandle () {
      this.selectRecords.forEach(item => {
        this.$store.commit('shopcart/DEL_SHOP_CART', item)
      })
      this.selectRecords = []
    },
    async handleClickCreateHandle () {
      if (this.componentType === 'workflowBtn' && this.orderSetResId) {
        this.doUpdateOrderSet()
      } else {
        this.doCreateOrderSet()
      }
    },
    async doCreateOrderSet () {
      const { displayname, name } = this.$store.getters.userInfo
      try {
        const resOrderSetM = new Manager('resource_order_sets')
        const shopCarts = this.selectRecords || []
        const shopCartIds = shopCarts.map(o => o.id)
        const orderSetData = {
          auto_execute: !this.isOpenOrderSetWorkflow,
          name: this.$t('common.shopcart_workflow_name', [displayname || name, this.$moment().format('YYYY-MM-DD'), uuid()]),
          parameters: shopCarts,
        }
        this.shopCartCreateLoading = true
        const orderSetRes = await resOrderSetM.create({ data: orderSetData })

        if (this.isOpenOrderSetWorkflow) {
          const variables = {
            process_definition_key: WORKFLOW_TYPES.EXECUTE_RESOURCE_ORDER_SET,
            initiator: this.userInfo.id,
            ids: orderSetRes.data.id,
            parameter: '{}',
            project: shopCarts[0].project,
            project_domain: shopCarts[0].project_domain,
          }
          await new this.$Manager('process-instances', 'v1').create({ data: { variables } })
          this.$store.commit('shopcart/SET_SHOP_CARTS', this.shopCartTableUserData.filter(o => {
            return !shopCartIds.includes(o.id)
          }))
          this.$message.success(this.$t('common.success'))
          this.$router.push('/workflow')
        } else {
          this.$store.commit('shopcart/SET_SHOP_CARTS', this.shopCartTableUserData.filter(o => {
            return !shopCartIds.includes(o.id)
          }))
          this.$message.success(this.$t('common.success'))
          this.$router.push('/vminstance')
        }
      } catch (error) {
        this.$message.error(this.$t('common.failed'))
        throw error
      } finally {
        this.selectRecords = []
        this.shopCartCreateLoading = false
      }
    },
    async doUpdateOrderSet () {
      const resOrderSetM = new Manager('resource_order_sets')
      const res = await resOrderSetM.get({ id: this.orderSetResId })
      if (res.data && res.data.parameters) {
        const data = [...res.data.parameters, ...this.selectRecords]
        await resOrderSetM.update({ id: this.orderSetResId, data: { parameters: data } })
      } else {
        this.$message.error(this.$t('common.failed'))
        throw new Error('Order set not found')
      }
      this.$message.success(this.$t('common.success'))
      this.visible = false
      this.$emit('update')
    },
  },
}
</script>

<style lang="less" scoped>
.shop-cart {
  .shop-cart-content {
    width: 556px;
    height: 330px;
    .top {
      padding-bottom: 6px;
      height: 39px;
      border-bottom: 1px solid rgba(233, 233, 233, 1);
    }
    .bottom {
      height: 302px;
      padding: 20px 0;
    }
    .no-data {
      padding: 15px;
      text-align: center;
      color: #999;
    }
  }
}
</style>
